import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { RootState } from "store/ducks";

import { formatDocument } from "utils/Document";

import attachmentIcon from "img/icon-anexo.svg";

import { FileTypesAccept } from "pages/OpenProtocol/interface";
import FloatingLabel from "components/FloatingLabel";
import { Column, Row } from "components/Layout/Grid";
import { Visibility } from "components/Visibility";
import RadioInput from "components/RadioInput";
import TextArea from "components/TextArea";
import { Button } from "components/Button";
import Loading from "components/Loading";
import Form from "components/Form";
import Text from "components/Text";

import TicketServices from "services/ticket.service";
import { TicketAdverseEventResponse, FileTypes } from "./interface";
import UploadService from "services/upload.service";
import { useTicketsContext } from "contexts/Tickets";
import Input from "components/Input";
import { PhoneNumberOutput } from "utils/PhoneNumber";
import { DateStringCustomPipefy, FormatDateString } from "utils/DateOutput";
import { formatAnvisaNumber } from "utils/Mask";

export const AdverseEventsForm: React.FC = () => {

  const dispatch = useDispatch();
  const ticketState = useSelector((state: RootState | undefined) => state?.slider?.state);

  const { reloadTableTicket } = useTicketsContext();

  const cardId = ticketState?.ticket.cardId;

  const [ticketAdverseEvent, setTicketAdverseEvent] = useState<TicketAdverseEventResponse>({
    isCustomerContact: true,
    protocolNumber: "",
    cpf: "",
    cnpj: "",
    customerName: "",
    email: "",
    phone: "",
    productName: "",
    regularizationNumber: "",
    productExpirationDate: "",
    productLot: "",
    description: "",
    productUseDate: "",
    justification: ""
  });

  const [files, setFiles] = useState<FileTypes | null>(null);
  const [filesMap, setFilesMap] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [isLoadingPage, setLoadingPage] = useState<boolean>(true);
  const [isLoadingButton, setLoadingButton] = useState<boolean>(false);

  const ticketService = new TicketServices();
  const _uploadService = new UploadService();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (cardId) {
      getTicketAdverseEventData();
    }
  }, [cardId]);

  useEffect(() => {
    updateFileDisplay();
  }, [files, ticketState?.ticket?.attachments]);

  const getTicketAdverseEventData = async () => {
    try {
      const response = await ticketService.getTicketAdverseEvent(cardId);
      if (response.data) {
        const options = response.data;

        setTicketAdverseEvent(options);

        if (options?.dataAuthorizationAttachment) {
          const fileAttachment = options.dataAuthorizationAttachment.split("|");
          const fileName = fileAttachment[0]?.trim();
          const fileUrl = fileAttachment[1]?.trim();
          const createFileObject = (name: string, url: string): FileTypes => {
            const blob = new Blob([""], { type: "application/octet-stream" });
            const file = new File([blob], name, { type: blob.type, lastModified: Date.now() });

            return Object.assign(file, { url });
          };

          const fileObj = createFileObject(fileName, fileUrl);
          setFiles(fileObj);
        }
        setLoadingPage(false);
      }
    } catch (error) {
      console.error('Erro ao buscar revisão do ticket', error);
      setLoadingPage(false);
    }
  };

  const handleFileUpload = async (file: FileTypes): Promise<FileTypes> => {
    if (FileTypesAccept.includes(file.type)) {
      return _uploadService.upload(file);
    }
    throw new Error("Invalid file type");
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target as HTMLSelectElement;
    const valueField = value === "true";

    setTicketAdverseEvent(prevState => ({
      ...prevState,
      isCustomerContact: valueField
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { value, dataset, name } = e.target;
    const valueField: string | number | boolean = value;
    const stateName = dataset.statename || name;

    setTicketAdverseEvent(prevState => ({
      ...prevState,
      [stateName]: valueField
    }));
  };

  async function addFile(e: React.ChangeEvent<HTMLInputElement>) {
    const fileTypes: Array<string> = FileTypesAccept;

    if (e.target.files && e.target.files.length > 0) {
      const arrayFiles: any = e.target.files[0];

      if (fileTypes.includes(arrayFiles.type)) {
        setFiles(arrayFiles);
      }
    }
  }

  function removeFile() {
    setFiles(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  const updateFileDisplay = () => {
    const newFilesMap = files ? [
      <FloatingLabel
        key="fileDisplay"
        label={files.name}
        onRemove={removeFile}
        uri={files.url}
      />
    ] : [];

    setFilesMap(newFilesMap);
  };

  const handleSubmit = async () => {
    setLoadingButton(true);

    try {

      let uploadedFileData = files ?? undefined;

      if (files && !files.url) {
        uploadedFileData = await handleFileUpload(files);
      }

      const payload = preparePayload(ticketAdverseEvent, uploadedFileData);

      const response = await ticketService.sendAdverseEvent(payload);
      if (response.status !== 200) {
        handleApiError(response);
      } else {
        dispatch({ type: 'CLOSE_SLIDER' });
        reloadTableTicket();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoadingButton(false);
      setFiles(null);
    }
  };

  const handleApiError = (error: any) => {
    const defaultMsg = "Erro ao registrar eventos adversos.";
    const errorMsg = error.response?.data?.data || error.data?.data || defaultMsg;

    setErrorMessage(errorMsg);
    setLoadingButton(false);
  };

  const preparePayload = (
    form: TicketAdverseEventResponse,
    fileData?: any
  ): any => {
    return {
      ticketId: ticketState?.ticket?.id ?? "",

      isCustomerContact: form.isCustomerContact,
      protocolNumber: form.protocolNumber,
      cnpj: form.cnpj,
      cpf: form.cpf,
      customerName: form.customerName,
      email: form.email,
      phone: form.phone,
      gender: form.gender,
      productName: form.productName,
      regularizationNumber: form.regularizationNumber,
      productExpirationDate: FormatDateString(form?.productExpirationDate || ""),
      productLot: form.productLot,
      description: form.description,
      productUseDate: FormatDateString(form?.productUseDate || ""),
      regularizationCompanyName: form.regularizationCompanyName,
      regularizationCompanyDocument: form.regularizationCompanyDocument,
      dataAuthorizationAttachment: fileData
        ? {
          name: fileData.name,
          url: fileData.uri || fileData.fileUrl || fileData.url,
        }
        : null,
      justification: form.justification,
      seriousAdverseEvent: ticketAdverseEvent.seriousAdverseEvent
    };
  };


  function getFormattedDocument(cpf?: string, cnpj?: string): string {
    return formatDocument(cpf || cnpj || '');
  }

  return (
    <Column className="animated fadeIn px-0 bg-white">
      <Visibility visible={isLoadingPage}>
        <Row className="align-center justify-center mt-10">
          <Loading onlySpinner size={50} />
        </Row>
      </Visibility>

      <Visibility visible={!isLoadingPage}>
        <Column className="mt-3">
          <Text
            className="mb-3 mt-2 mr-4 color-blue-gray"
            size={14}
            weight={"bold"}
          >
            Foi possível realizar o contato com o cliente? *
          </Text>

          <Row className="align-center">
            <Row className="w-md-100 mt-md-2 mr-4 ">
              <RadioInput
                id="mustShippingCompanyPayBack-Yes"
                checked={ticketAdverseEvent?.isCustomerContact === true}
                onClick={handleSelectChange}
                name="isCustomerContact"
                defaultValue={true}
                required
              >
                <Text className="color-blue-gray">Sim</Text>
              </RadioInput>
            </Row>
            <Row className="w-md-100 mt-md-2">
              <RadioInput
                id="mustShippingCompanyPayBack-No"
                checked={ticketAdverseEvent?.isCustomerContact === false}
                onClick={handleSelectChange}
                name="isCustomerContact"
                defaultValue={false}
                required
              >
                <Text className="color-blue-gray">Não</Text>
              </RadioInput>
            </Row>
          </Row>
        </Column>

        <Row
          className="align-center flex-wrap bg-white py-3 grid-gap-2"
          style={{ borderRadius: "10px 10px 0px 0px" }}
        >
          <Column className="mr-2 mr-md-2">
            <Text className="mr-2 mb-1 color-blue-gray" size={14} weight="bold">
              Protocolo
            </Text>
            <Row className="flex-1 align-center grid-gap-3 flex-wrap">
              <Row className="flex-1 align-center">
                <Text className="mr-2 mb-1 color-blue-gray" size={16}>
                  {ticketAdverseEvent?.protocolNumber}
                </Text>
              </Row>
            </Row>
          </Column>

          <Column className="mr-2 mr-md-2">
            <Text className="mr-2 mb-1 color-blue-gray" size={14}>
              {!!ticketAdverseEvent?.cnpj ? "CNPJ" : "CPF"}
            </Text>
            <Row className="flex-1 align-center grid-gap-3 flex-wrap">
              <Row className="flex-1 align-center">
                <Text className="mr-2 mb-1 color-blue-gray" size={16}>
                  {getFormattedDocument(ticketAdverseEvent?.cpf, ticketAdverseEvent?.cnpj)}
                </Text>
              </Row>
            </Row>
          </Column>
          <Column className="mr-2 mr-md-2">
            <Text className="mr-2 mb-1 color-blue-gray" size={14} weight="bold">
              Cliente
            </Text>
            <Row className="flex-1 align-center grid-gap-3 flex-wrap">
              <Row className="flex-1 align-center">
                <Text className="mr-2 mb-1 color-blue-gray" size={16}>
                  {ticketAdverseEvent?.customerName}
                </Text>
              </Row>
            </Row>
          </Column>
        </Row>

        <Form
          id="form_adverse_events"
          onValidate={handleSubmit}
          customFunction={() => { }}
          disableEnterSubmit={true}
        >
          <Visibility visible={ticketAdverseEvent.isCustomerContact}>

            <div>
              <Row className="grid-gap-4 mb-3 w-100 flex-wrap">
                <Column className="flex-3 w-md-100">
                  <Text className="color-blue-gray mb-1">E-mail do consumidor*</Text>
                  <Input
                    required
                    onChange={handleInputChange}
                    className="w-100"
                    stateName="email"
                    name="E-mail"
                    type="email"
                    value={ticketAdverseEvent?.email ?? ""}
                    placeholder="E-mail"
                  />
                </Column>

                <Column className="flex-2 w-md-100">
                  <Text className="color-blue-gray mb-1">Telefone*</Text>
                  <Input
                    id="telephone-new-customer"
                    required
                    onChange={handleInputChange}
                    className="w-100"
                    stateName="phone"
                    name="Telefone"
                    type="text"
                    value={PhoneNumberOutput(ticketAdverseEvent?.phone)}
                    placeholder="Telefone"
                  />
                </Column>

                <Column className="flex-2 w-md-100">
                  <Text className="mb-2 mr-4 color-blue-gray">Sexo?* </Text>
                  <Row className="align-center relative">
                    <Row className="w-md-100 mr-4">
                      <RadioInput
                        id="gender-M"
                        checked={ticketAdverseEvent?.gender?.toUpperCase() === "MASCULINO"}
                        onChange={handleInputChange}
                        name="Sexo"
                        value="Masculino"
                        showError={true}
                        stateName="gender"
                        required
                      >
                        <Text className="color-blue-gray">Masculino</Text>
                      </RadioInput>
                    </Row>
                    <Row className="w-md-100">
                      <RadioInput
                        id="gender-F"
                        checked={ticketAdverseEvent?.gender?.toUpperCase() === "FEMININO"}
                        onChange={handleInputChange}
                        name="Sexo"
                        value="Feminino"
                        showError={false}
                        stateName="gender"
                        required
                      >
                        <Text className="color-blue-gray">Feminino</Text>
                      </RadioInput>
                    </Row>
                  </Row>
                </Column>
              </Row>

              <Text className="font-bold py-4"> Identificação do produto cosmético suspeito</Text>

              <Column className="mb-4">
                <Text className="color-blue-gray mb-1">Nome comercial do produto</Text>
                <Input
                  className="w-100"
                  placeholder="Nome comercial do produto"
                  stateName="productName"
                  name="Nome comercial do produto"
                  type="text"
                    value={ticketAdverseEvent.productName ?? ""}
                  onChange={handleInputChange}
                  disabled
                />
              </Column>

              <Row className="grid-gap-4 w-90 flex-wrap mb-4">
                <Column className="flex-3">
                  <Text className="color-blue-gray mb-1">Nº de regularização ANVISA*</Text>
                  <Input
                    name="Nº de regularização ANVISA"
                    stateName="regularizationNumber"
                    className="w-100"
                    value={formatAnvisaNumber(ticketAdverseEvent.regularizationNumber ?? "")}
                    type="text"
                    onChange={handleInputChange}
                    placeholder="Nº de regularização ANVISA"
                    required
                  />
                </Column>
                <Column className="flex-2">
                  <Text className="color-blue-gray mb-1">Prazo de validade*</Text>
                  <Input
                    required
                    className="w-100"
                    value={DateStringCustomPipefy(ticketAdverseEvent?.productExpirationDate || "")}
                    onChange={handleInputChange}
                    type="date"
                    name="Prazo de validade"
                    max={"9999-01-01"}
                    stateName="productExpirationDate"
                    placeholder="Prazo de validade"
                  />
                </Column>
                <Column className="flex-2">
                  <Text className="color-blue-gray mb-1">Lote*</Text>
                  <Input
                    name="Lote"
                    stateName="productLot"
                    className="w-100"
                    placeholder="Lote"
                    value={ticketAdverseEvent.productLot ?? ""}
                    onChange={handleInputChange}
                    type="text"
                    maxLength={255}
                    minLength={3}
                    required
                  />
                </Column>
              </Row>

              <Column className="mb-4 relative">
                <Text className="mb-2 color-blue-gray" size={12}>
                  Descrição do evento adverso*
                </Text>
                <TextArea
                  minLength={7}
                  name="Descrição do evento adverso"
                  stateName="description"
                  placeholder="Descrição"
                  onChange={handleInputChange}
                  style={{ minHeight: 150 }}
                  value={ticketAdverseEvent?.description || ""}
                  maxLength={1024}
                  required
                />
              </Column>

              <Column>
                <Text className="mb-2 color-blue-gray" size={12}>
                  Data de uso do produto*
                </Text>
                <Input
                  required
                  className="w-20 w-lg-30"
                  value={DateStringCustomPipefy(ticketAdverseEvent?.productUseDate || "")}
                  onChange={handleInputChange}
                  type="date"
                  max={"9999-01-01"}
                  name="Data de uso do produto"
                  stateName="productUseDate"
                  placeholder="Data de uso do produto"
                />
              </Column>

              <Text className="font-bold py-4"> Identificação da empresa titular da regularização dos produtos cosmético</Text>

              <Row className="grid-gap-4 mb-3 w-70 flex-wrap">
                <Column className="flex-3">
                  <Text className="color-blue-gray mb-1">Nome da empresa*</Text>
                  <Input
                    name="Nome da empresa"
                    stateName="regularizationCompanyName"
                    className="w-100"
                    placeholder="Nome da empresa*"
                    value={ticketAdverseEvent.regularizationCompanyName ?? ""}
                    onChange={handleInputChange}
                    type="text"
                    maxLength={255}
                    required
                  />
                </Column>
                <Column className="flex-2">
                  <Text className="color-blue-gray mb-1">CNPJ*</Text>
                  <Input
                    name="CNPJ"
                    stateName="regularizationCompanyDocument"
                    className="w-100"
                    placeholder="CNPJ"
                    value={formatDocument(ticketAdverseEvent.regularizationCompanyDocument ?? "")}
                    onChange={handleInputChange}
                    type="text"
                    required
                  />
                </Column>
              </Row>

              <Column className="mt-5">
                <Row className="align-center">
                  <img src={attachmentIcon} alt="" />
                  <Text className="ml-1 color-blue-gray" size={12}>
                     Anexar autorização de uso de dados (LGPD)*
                  </Text>
                </Row>
                <Row className="mt-1 align-center align-sm-none flex-sm-column  ">
                  <label
                    className="button outline"
                    htmlFor="open_protocol_files"
                    id="select-file"
                  >
                    <Text className="px-2 color-primary font-bold">
                      Selecionar Arquivo
                    </Text>
                  </label>
                  <Row className="ml-2 ml-sm-0 my-sm-2 align-center flex-wrap word-break">
                    {filesMap}
                  </Row>
                </Row>
                <Input
                  ref={fileInputRef}
                  onChange={addFile}
                  id="open_protocol_files"
                  className="m-0 p-0 h-0 absolute"
                  type="file"
                  required={!files}
                  name="Anexar autorização de dados"
                  stateName="dataAuthorizationAttachment"
                  accept={'application/pdf'}
                  style={{ visibility: "hidden" }}
                />
              </Column>
            </div>
          </Visibility>

          <Visibility visible={!ticketAdverseEvent.isCustomerContact}>
            <Column className="mb-4 relative">
              <Text className="mb-2 color-blue-gray" size={12}>
                Justificativa pela falta de contato*
              </Text>
              <TextArea
                stateName="justification"
                minLength={7}
                name="Justificativa pela falta de contato"
                placeholder="Descrição"
                value={ticketAdverseEvent?.justification || ""}
                onChange={handleInputChange}
                style={{ minHeight: 150 }}
                required
              />
            </Column>
          </Visibility>

          <Column className="mt-4">

            {errorMessage && (
              <Text className="color-danger mt-2 animated fadeIn" size={14}>
                {errorMessage}
              </Text>
            )}
            <Row className="my-3 flex-1 justify-center align-center">
              <Button
                form="form_adverse_events"
                isRequesting={isLoadingButton}
                disabled={isLoadingButton}
                spinnerColor="#0E4C54"
                className="outline bg-primary flex-sm-1 align-center justify-center d-flex"
              >
                <Text className="color-primary font-bold px-3">
                  Registrar dados
                </Text>
              </Button>
            </Row>
          </Column>
        </Form>
      </Visibility>
    </Column>
  );
};